import type { FC } from "react";
import { css, cx } from "emotion";
import { useCallback, useMemo, useRef, useState } from "react";
import { Upload } from "antd";
import { ToastTypes, showToast } from "@certa/catalyst/components/Toast";
import {
  ButtonVariants,
  Button,
  ButtonTypes
} from "@certa/catalyst/components/Button";
import { SubscribeSwitch } from "./SubscribeSwitch";
import type { MentionItem, MentionsInputProps } from "react-mentions";
import { useMentions } from "../../hooks/useMentions";
import {
  invalidateChannelsQuery,
  useAddCommentMutation
} from "@certa/queries/hooks/comments.hooks";
import { useIntl } from "react-intl";
import {
  useChannelContext,
  useThreadContext,
  useCommentsContext
} from "../../comments.context";
import { Attachment } from "@certa/icons/components/Attachment";
import { Close } from "@certa/icons/components/Close";
import { Stack as StackOld, Text, Tooltip } from "@certa/blocks/thanos";
import { Stack } from "@certa/catalyst/layouts/Stack";
import type { UploadFileBlob } from "./AttachementViewModal";
import { AttachmentViewModal } from "./AttachementViewModal";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import { useCommentsDrawerContext } from "../../CommentsDrawerContext";
import { MentionSupportedInput } from "./MentionSupportedInput/MentionSupportedInput";
import { useSubscribeThreadToggle } from "@certa/common/hooks/useCanSubscribeThread";
import { validateUploadFile } from "@certa/common/utils/file";
import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { WrapWithMigrationTooltip } from "@certa/common/components/WrapWithMigrationTooltip";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import {
  invalidateCommsConversation,
  invalidateConversationMessages,
  type ConversationMessagesModel
} from "@certa/queries/queries/comms";

import { useOptimisticMessageHandling } from "../../hooks/useOptimisticMessageHandling";
import {
  type OptimisticConversationMessagesModel,
  type OptimisticId
} from "../../util/optimisticMessage.utils";
import { CircleArrowUpFilled } from "@certa/icons/components/CircleArrowUpFilled";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import {
  Typography,
  TypographyVariants
} from "@certa/catalyst/components/Typography";

/**
 * TODO: Component to be broken down further, with possibly the modal part
 * extracted out.
 */

export const CommentFormNew: FC<{
  onCommentPost?: () => void;
  // From Query Param
  addOptimisticMessage: (message: OptimisticConversationMessagesModel) => void;
  removeOptimisticMessage: (messageId: OptimisticId) => void;
  replaceOptimisticMessage: (
    optimisticId: string,
    actualMessage: ConversationMessagesModel
  ) => void;
}> = ({
  onCommentPost,
  addOptimisticMessage,
  removeOptimisticMessage,
  replaceOptimisticMessage
}) => {
  const { objectType, objectId, fieldId } = useChannelContext();
  const { threadId, messageCount, threadUserGroups } = useThreadContext();
  const availableMentions = useMentions(threadId);
  const { activeConversationThreadComms } = useCommentsContext();
  const activeConversationThreadCommsId =
    activeConversationThreadComms?.conversationId;
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  const { isWorkflowBeingMigrated, parentThreadObject } =
    useCommentsDrawerContext();

  // Optimistic message functions are now passed as props from ThreadContainer
  const {
    handleOptimisticAdd,
    handleOptimisticReplace,
    handleOptimisticRemove,
    isOptimisticEnabled
  } = useOptimisticMessageHandling(
    activeConversationThreadCommsId,
    addOptimisticMessage,
    removeOptimisticMessage,
    replaceOptimisticMessage
  );

  const [message, setMessage] = useState(""); // To store message text
  const [mentions, setMentions] = useState<MentionItem[]>([]); // To store mentioned users and groups
  const [attachment, setAttachment] = useState<UploadFileBlob | null>(null); // To store the attachment that needs to be uploaded
  const [isModalVisible, setIsModalVisibility] = useState(false); //
  const { mutate: addComment, isLoading } = useAddCommentMutation(
    fieldId || objectId, // Refer to comment.hooks on why we did this.
    objectType,
    parentThreadObject
  );
  const canRenderSubscribeSwitch = useSubscribeThreadToggle();

  const intl = useIntl();
  const { isMobileResolution } = useScreenResolution();

  const setModalVisible = (value: boolean) => {
    setIsModalVisibility(value);
    if (!value) {
      /**
       *  Bug: rc-util is adding overflow: hidden on body tag,
       *  remove once antd is upgraded. For now we reset this style
       *  manually.
       */
      setTimeout(() => {
        const body: HTMLElement | null = document.querySelector("body");
        if (body) {
          body.style.removeProperty("overflow");
        }
      }, 1000);
    }
  };
  /**
   * This method helps in attaching images from clipboard, usually screenshots.
   */
  const handlePaste = useCallback((event: any) => {
    try {
      if (
        event.clipboardData.files.length &&
        event.clipboardData.files[0].type.startsWith("image/")
      ) {
        // Get the first file (if you've got multiple here)
        const file = event.clipboardData.files[0];
        // make sure to attach UID to it, as ANTD Uploader uses
        // it as key when it renders the file name
        file.uid = "image" + Date.now();

        // update the file in the state.
        setAttachment(file);
        setModalVisible(true);
      }
    } catch (err) {
      // Prevent Oops screen by handing any error here.
      console.error("Failed to attach clipboard data", err);
    }
  }, []);
  /**
   * If the message is empty or there's no attachment, we
   * disable the post button altogether
   */
  const isPostDisabled = useMemo(
    () => !message.trim() && !attachment,
    [attachment, message]
  );

  const isSendMessageDisabled =
    isPostDisabled || isWorkflowBeingMigrated || isLoading;

  const shouldInvalidateChannelsQuery = messageCount === 0;

  const inputRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Post CTA handles the preparation of payload where user mentions,
   * group mentions, attachment and the message itself is considered
   * and pushed to the thread via thunk.
   */
  const handlePostComment = useCallback(async () => {
    analyticsService.track(
      AnalyticsEvents.workflowDetailEvents
        .COMMENT_DETAIL_PANE_CLICK_SUBMIT_COMMENT
    );
    if (isSendMessageDisabled) {
      showToast({
        type: ToastTypes.ERROR,
        title: intl.formatMessage({
          id: "comments.errors.commentPostFailed"
        })
      });
      return;
    }
    setModalVisible(false);

    const mentionedUsers: number[] = [];
    const mentionedGroups: number[] = [];

    // Going through all the mentions, breaking them
    // into users and groups
    mentions.forEach(mention => {
      // id is in format of u123 or g123, depending
      // upon whether it's a user or a group.
      const realId = parseInt(mention.id.substr(1));
      if (mention.id.indexOf("u") === 0) {
        // it's a user mention
        mentionedUsers.push(realId);
      } else {
        // it's a group mention
        mentionedGroups.push(realId);
      }
    });

    const commentPayload = {
      object_id: objectId,
      type: objectType,
      mentioned_users: mentionedUsers,
      mentioned_groups: mentionedGroups,
      message: message.trim() || "",
      attachment: attachment as unknown as File,
      thread_id: threadId
    };

    // Add optimistic message for comms endpoints without attachments
    let optimisticMessageId: OptimisticId | null = null;
    const previousMessage = message;
    const previousMentions = mentions;
    if (isOptimisticEnabled && !attachment) {
      optimisticMessageId = handleOptimisticAdd({
        messageContent: commentPayload.message,
        mentionedUsers,
        mentionedGroups,
        hasAttachment: !!attachment
      });

      // Clear form immediately for optimistic updates
      if (optimisticMessageId) {
        setMessage("");
        setMentions([]);
      }
    }

    addComment(commentPayload, {
      onError: async e => {
        // Remove optimistic message on error
        if (optimisticMessageId) {
          handleOptimisticRemove(optimisticMessageId);
        }

        // Restore message and mentions on error
        setMessage(previousMessage);
        setMentions(previousMentions);

        try {
          const error = e as Response;
          const errorFromAPI = await error.json();

          if (errorFromAPI.errors?.non_field_errors?.length > 0) {
            showToast({
              type: ToastTypes.ERROR,
              title: errorFromAPI.errors.non_field_errors[0]
            });
            return;
          }

          showToast({
            type: ToastTypes.ERROR,
            title: errorFromAPI.errors.attachment[0]
          });
        } catch (e) {
          showToast({
            type: ToastTypes.ERROR,
            title: intl.formatMessage({
              id: "comments.errors.commentPostFailed"
            })
          });
        }
      },
      onSuccess: data => {
        // Replace optimistic message with real server response for comms endpoints
        if (optimisticMessageId && isOptimisticEnabled) {
          handleOptimisticReplace(optimisticMessageId, data);
        }

        // Clear form for non-optimistic or attachment cases
        if (!isOptimisticEnabled || !!attachment) {
          setMessage("");
          setMentions([]);
        }

        setAttachment(null);
        // Refetch conversation messages if comms endpoints are enabled
        if (isCommsNewEndpointEnabled && activeConversationThreadCommsId) {
          invalidateConversationMessages(activeConversationThreadCommsId);
          invalidateCommsConversation();
          if (shouldInvalidateChannelsQuery) {
            invalidateChannelsQuery(objectId, objectType);
          }
        }
        onCommentPost?.();

        setTimeout(() => inputRef.current?.focus(), 0);
      }
    });
  }, [
    mentions,
    isSendMessageDisabled,
    objectId,
    objectType,
    message,
    attachment,
    threadId,
    addComment,
    intl,
    onCommentPost,
    handleOptimisticAdd,
    handleOptimisticReplace,
    handleOptimisticRemove,
    isOptimisticEnabled,
    isCommsNewEndpointEnabled,
    activeConversationThreadCommsId,
    shouldInvalidateChannelsQuery
  ]);

  /**
   * Update message within the component's state
   */
  const onMessageChange = useCallback<
    NonNullable<MentionsInputProps["onChange"]>
  >((event, value, rawValue, mentions) => {
    setMessage(value);
    setMentions(mentions);
  }, []);

  /**
   * Update attachment within the component's state
   * @returns {boolean} false, to stop backgroup upload API action by uploader component
   */
  const handleBeforeUpload = useCallback((file: any) => {
    const { valid: isFileValid, error } = validateUploadFile({ file });

    if (isFileValid) {
      setAttachment(file);
      setModalVisible(true);
    } else {
      showToast({
        type: ToastTypes.ERROR,
        title: String(error)
      });
    }

    return false; // prevent uploading
  }, []);

  /**
   * Removes attachment from the state
   */
  const handleOnRemove = useCallback((file: any) => {
    setAttachment(null);
  }, []);

  /**
   * Hides modal and removes the attachment
   */
  const handleModalCancel = useCallback(() => {
    setAttachment(null);
    setModalVisible(false);
  }, []);

  /**
   * Upload component props to handle callbacks for certain events
   * and the file list.
   */
  const uploadProps = useMemo(() => {
    return {
      onRemove: handleOnRemove,
      beforeUpload: handleBeforeUpload,
      fileList: attachment ? [attachment] : []
    };
  }, [attachment, handleOnRemove, handleBeforeUpload]);

  // We're using this common element within the Comment Drawer as well as
  // Image attachment modal preview.
  const inputElement = (
    <MentionSupportedInput
      ref={inputRef}
      onChange={onMessageChange}
      disabled={isLoading || !!isWorkflowBeingMigrated}
      message={message}
      mentions={availableMentions}
      threadId={threadId}
      onSubmit={handlePostComment}
      autoFocus
    />
  );

  // const handleMentionSupport = () => {
  //   const certaBotUser = availableMentions.find(
  //     mention => mention.isBot === true
  //   );
  //   const botTag = `~[${certaBotUser?.display}](${certaBotUser?.id})`;
  //   setMessage(prevMessage =>
  //     prevMessage ? `${prevMessage} ${botTag} ` : `${botTag} `
  //   );
  //   //@ts-expect-error
  //   setMentions(prevValue => [
  //     ...prevValue,
  //     { id: certaBotUser?.id, display: certaBotUser?.display }
  //   ]);
  // };

  // E.g. Admin, Certa-Requestor, L1 Approver-Read only, L2 Approver-Read Only and 3 more
  const visibleToText =
    threadUserGroups.length > 4
      ? `${threadUserGroups[0].name}, ${threadUserGroups[1].name}, ${threadUserGroups[2].name}, ${threadUserGroups[3].name} and ${threadUserGroups.length - 4} more`
      : threadUserGroups.length > 1
        ? `${threadUserGroups
            .slice(0, -1)
            .map(group => group.name)
            .join(
              ", "
            )} and ${threadUserGroups[threadUserGroups.length - 1].name}`
        : threadUserGroups[0]?.name || "";

  return (
    <div onPaste={handlePaste}>
      <Stack
        direction="vertical"
        gap="16"
        dangerouslySetClassName={cx(
          "hide-print",
          css({
            position: "relative"
          })
        )}
      >
        {!!attachment && isModalVisible && (
          <AttachmentViewModal
            visible={isModalVisible}
            attachment={attachment}
            onOk={() => {
              analyticsService.track(
                AnalyticsEvents.workflowDetailEvents
                  .COMMENT_DETAIL_PANE_CLICK_ATTACH_FILE
              );
              handlePostComment();
            }}
            onCancel={handleModalCancel}
            isLoading={isLoading}
            isDisabled={isPostDisabled || isLoading}
          >
            <StackOld
              gap="s2"
              direction="vertical"
              className={css`
                position: relative;
                ${attachment
                  ? `
                .comments-textarea-new__input,  
                .comments-textarea-new__highlighter {
                    padding-bottom: 44px !important;
                }
              `
                  : ``}
              `}
            >
              {inputElement}
              {attachment ? (
                <Tooltip title={attachment.name}>
                  <StackOld
                    gap="s1"
                    align="center"
                    className={css`
                      padding: 4px 6px;
                      background-color: var(--neutral-20);
                      border-radius: var(--small-border-radius);
                      position: absolute;
                      bottom: 0.75rem;
                      left: 0.75rem;
                    `}
                  >
                    <Text
                      variant="p1-bold"
                      color="neutral-100"
                      ellipsis={200}
                      useMaxWidth={true}
                    >
                      {attachment.name}
                    </Text>
                    <Close
                      disabled={isWorkflowBeingMigrated}
                      size={10}
                      color="neutral-70"
                      onClick={handleModalCancel}
                      style={{
                        cursor: "pointer"
                      }}
                    />
                  </StackOld>
                </Tooltip>
              ) : null}
            </StackOld>
          </AttachmentViewModal>
        )}

        <Stack
          gap="4"
          direction="vertical"
          dangerouslySetClassName={css({
            position: "relative",
            ".comments-textarea-new": {
              width: "100%"
            }
          })}
        >
          <Stack direction="horizontal" justify="center">
            <Typography
              variant={TypographyVariants.LABEL_SM}
              color={CatalystColors.NEUTRAL_600}
            >
              Visible to {visibleToText}
            </Typography>
          </Stack>

          <WrapWithMigrationTooltip show={!!isWorkflowBeingMigrated}>
            {inputElement}
          </WrapWithMigrationTooltip>

          <Stack
            direction="horizontal"
            align="center"
            justify="space-between"
            wrap="wrap"
            dangerouslySetClassName={css({
              position: "absolute",
              bottom: "0.25rem",
              right: "0.25rem"
            })}
          >
            <Stack
              direction="horizontal"
              gap="8"
              padding="8"
              align="center"
              dangerouslySetClassName={css({
                marginTop: isMobileResolution ? "var(--s2)" : undefined
              })}
            >
              <Upload
                {...uploadProps}
                disabled={isLoading}
                className={css({
                  ".ant-upload:focus-visible button": {
                    outline: `2px solid ${CatalystColors.BRAND_400} !important`
                  },
                  ".ant-upload-list": {
                    display: "none"
                  }
                })}
              >
                <WrapWithMigrationTooltip show={!!isWorkflowBeingMigrated}>
                  <Button
                    tabIndex={-1}
                    type={ButtonTypes.ICON}
                    rightIcon={<Attachment />}
                    disabled={isWorkflowBeingMigrated}
                    aria-label="Attach File"
                  />
                </WrapWithMigrationTooltip>
              </Upload>
              <WrapWithMigrationTooltip show={!!isWorkflowBeingMigrated}>
                <SendButton isLoading={isLoading} onClick={handlePostComment} />
              </WrapWithMigrationTooltip>
            </Stack>
          </Stack>
        </Stack>

        {canRenderSubscribeSwitch && <SubscribeSwitch />}
      </Stack>
    </div>
  );
};

const SendButton = ({
  isLoading,
  onClick
}: {
  isLoading: boolean;
  onClick: () => void;
}) => {
  return isLoading ? (
    <Button
      variant={ButtonVariants.ROUNDED}
      type={ButtonTypes.ICON}
      loading={true}
      aria-label="Send"
    />
  ) : (
    // Custom button
    <button
      aria-label="Send"
      className={css({
        all: "unset",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        borderRadius: "100px"
      })}
      onClick={onClick}
    >
      <CircleArrowUpFilled size={24} color={CatalystColors.BRAND_400} />
    </button>
  );
};
